import React, { useMemo } from 'react';

// Helper for smooth cubic bezier path
const getSmoothPath = (points, height) => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M${points[0].x},${points[0].y}`;

  // Simple control point calculation
  const controlPoint = (current, previous, next, reverse) => {
    const p = previous || current;
    const n = next || current;
    const smoothing = 0.2;
    const o = {
      x: n.x - p.x,
      y: n.y - p.y
    };
    const angle = Math.atan2(o.y, o.x);
    const length = Math.sqrt(Math.pow(o.x, 2) + Math.pow(o.y, 2)) * smoothing;
    const x = current.x + Math.cos(angle + (reverse ? Math.PI : 0)) * length;
    const y = current.y + Math.sin(angle + (reverse ? Math.PI : 0)) * length;
    return { x, y };
  };

  let d = `M${points[0].x},${points[0].y}`;
  
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2];

    const cp1 = controlPoint(p1, p0, p2, false);
    const cp2 = controlPoint(p2, p1, p3, true);

    d += ` C${cp1.x},${cp1.y} ${cp2.x},${cp2.y} ${p2.x},${p2.y}`;
  }
  return d;
};

const TrendChart = ({ points, prevPoints = [] }) => {
  const { pathCurrent, areaCurrent, pathPrev, maxValue } = useMemo(() => {
    const safePoints = Array.isArray(points) ? points : [];
    const safePrevPoints = Array.isArray(prevPoints) ? prevPoints : [];
    
    // Normalize values
    const currentValues = safePoints.map((p) => (typeof p?.value === 'number' ? p.value : 0));
    const prevValues = safePrevPoints.map((p) => (typeof p?.value === 'number' ? p.value : 0));
    
    const allValues = [...currentValues, ...prevValues];
    const max = Math.max(1, ...allValues);
    const n = Math.max(safePoints.length, safePrevPoints.length);

    const getCoords = (data) => {
      if (data.length <= 1) {
        const v = data.length === 1 ? data[0] : 0;
        const y = 100 - (v / max) * 100;
        return [{ x: 0, y }, { x: 100, y }];
      }
      return data.map((v, idx) => ({
        x: (idx / (n - 1)) * 100,
        y: 100 - (v / max) * 100
      }));
    };

    const coordsCurrent = getCoords(currentValues);
    const coordsPrev = getCoords(prevValues);

    const pathC = getSmoothPath(coordsCurrent, 100);
    const pathP = getSmoothPath(coordsPrev, 100);

    // Area needs to close the loop
    const areaC = coordsCurrent.length > 0 
      ? `${pathC} L${coordsCurrent[coordsCurrent.length-1].x},100 L${coordsCurrent[0].x},100 Z`
      : '';

    return { 
      pathCurrent: pathC, 
      areaCurrent: areaC, 
      pathPrev: pathP, 
      maxValue: max 
    };
  }, [points, prevPoints]);

  const labels = useMemo(() => {
    const safePoints = Array.isArray(points) ? points : [];
    if (safePoints.length === 0) return { left: '', middle: '', right: '' };
    if (safePoints.length === 1) return { left: safePoints[0]?.label || '', middle: '', right: '' };
    const midIdx = Math.floor((safePoints.length - 1) / 2);
    return {
      left: safePoints[0]?.label || '',
      middle: safePoints[midIdx]?.label || '',
      right: safePoints[safePoints.length - 1]?.label || ''
    };
  }, [points]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-xs text-white/70">Tendencia</div>
        <div className="text-xs text-white/70">Max {maxValue}</div>
      </div>
      <div className="mt-3 rounded-[18px] border border-white/10 bg-white/5 p-3">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-28 overflow-visible">
          {/* Previous period line (dashed) */}
          {pathPrev && (
            <path
              d={pathPrev}
              fill="none"
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="2"
              strokeDasharray="4 4"
              vectorEffect="non-scaling-stroke"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          
          {/* Current period area */}
          <path d={areaCurrent} fill="rgba(255,255,255,0.08)" />
          
          {/* Current period line */}
          <path
            d={pathCurrent}
            fill="none"
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div className="mt-2 flex items-center justify-between text-[11px] text-white/60">
          <div>{labels.left}</div>
          <div>{labels.middle}</div>
          <div>{labels.right}</div>
        </div>
      </div>
    </div>
  );
};

export default TrendChart;

